import { getActiveLocale } from "./locale";

/**
 * Hardcoded, non-narrative UI microcopy that lives in shared/device
 * components rather than a scenario's beat tree — the scenario-overlay
 * locale system (`extract-i18n.mjs` / `locales/*.json`) only ever sees
 * `ScenarioSpec` fields, so strings baked directly into component JSX
 * (button labels, hints, tooltips) are invisible to it and were the other
 * half of the 2026-07-26 "50% untranslated" report alongside the missing
 * `ALLOW_KEYS`. Keep this file small and generic (UI chrome only, never
 * course narrative — that belongs in a scenario field so it can be
 * per-course and per-language authored, not hardcoded here).
 */

type Locale = "en" | "de" | "fr" | "es";

const STRINGS: Record<string, Record<Locale, string>> = {
  "usb.momentLater": {
    en: "A moment later",
    de: "Einen Moment später",
    fr: "Un instant plus tard",
    es: "Un momento después",
  },
  "usb.tapNote": {
    en: "Tap the note to read it",
    de: "Tippen, um die Notiz zu lesen",
    fr: "Touchez la note pour la lire",
    es: "Toca la nota para leerla",
  },
  "usb.dragPrompt": {
    en: "Drag the USB drive to what you'd do with it",
    de: "Ziehe den USB-Stick auf das, was du damit tun würdest",
    fr: "Faites glisser la clé USB vers ce que vous en feriez",
    es: "Arrastra la memoria USB a lo que harías con ella",
  },
  "usb.drive": {
    en: "USB drive",
    de: "USB-Stick",
    fr: "Clé USB",
    es: "Memoria USB",
  },
  "walk.controls": {
    en: "Controls",
    de: "Steuerung",
    fr: "Contrôles",
    es: "Controles",
  },
  "walk.walk": {
    en: "Walk",
    de: "Gehen",
    fr: "Marcher",
    es: "Caminar",
  },
  "walk.look": {
    en: "Look",
    de: "Umschauen",
    fr: "Regarder",
    es: "Mirar",
  },
  "walk.forward": {
    en: "Walk forward (W)",
    de: "Vorwärtsgehen (W)",
    fr: "Avancer (W)",
    es: "Avanzar (W)",
  },
  "walk.back": {
    en: "Walk back (S)",
    de: "Rückwärtsgehen (S)",
    fr: "Reculer (S)",
    es: "Retroceder (S)",
  },
  "walk.stepLeft": {
    en: "Step left (A)",
    de: "Nach links (A)",
    fr: "Pas à gauche (A)",
    es: "Paso a la izquierda (A)",
  },
  "walk.stepRight": {
    en: "Step right (D)",
    de: "Nach rechts (D)",
    fr: "Pas à droite (D)",
    es: "Paso a la derecha (D)",
  },
  "walk.lookUp": {
    en: "Look up",
    de: "Nach oben schauen",
    fr: "Regarder en haut",
    es: "Mirar hacia arriba",
  },
  "walk.lookDown": {
    en: "Look down",
    de: "Nach unten schauen",
    fr: "Regarder en bas",
    es: "Mirar hacia abajo",
  },
  "walk.turnLeft": {
    en: "Turn left",
    de: "Nach links drehen",
    fr: "Tourner à gauche",
    es: "Girar a la izquierda",
  },
  "walk.turnRight": {
    en: "Turn right",
    de: "Nach rechts drehen",
    fr: "Tourner à droite",
    es: "Girar a la derecha",
  },
  "walk.nothingNearby": {
    en: "Nothing to interact with nearby",
    de: "Nichts in der Nähe zum Interagieren",
    fr: "Rien à proximité avec quoi interagir",
    es: "No hay nada cerca con lo que interactuar",
  },
  "walk.walkCloser": {
    en: "Walk closer to the desk to interact",
    de: "Gehe näher an den Schreibtisch, um zu interagieren",
    fr: "Approchez-vous du bureau pour interagir",
    es: "Acércate al escritorio para interactuar",
  },
  "walk.captureMouse": {
    en: "Capture mouse",
    de: "Maus fangen",
    fr: "Capturer la souris",
    es: "Capturar el ratón",
  },
  "walk.captureMouseTitle": {
    en: "Capture the mouse for smooth FPS-style look (Esc releases it)",
    de: "Maus fangen für flüssiges FPS-Umschauen (Esc gibt sie wieder frei)",
    fr: "Capturer la souris pour un regard fluide façon FPS (Échap la libère)",
    es: "Captura el ratón para mirar de forma fluida estilo FPS (Esc la libera)",
  },
  "walk.dragHint": {
    en: "Drag the room to look around \u00b7 click items to inspect",
    de: "Raum ziehen, um sich umzuschauen \u00b7 Objekte anklicken, um sie zu untersuchen",
    fr: "Faites glisser la pièce pour regarder autour de vous \u00b7 cliquez sur les objets pour les examiner",
    es: "Arrastra la sala para mirar alrededor \u00b7 haz clic en los objetos para examinarlos",
  },
  "walk.mouseCaptured": {
    en: "Mouse captured — Esc frees it. Aim the circle at the note and click to read it.",
    de: "Maus gefangen — Esc gibt sie frei. Ziele mit dem Kreis auf die Notiz und klicke, um sie zu lesen.",
    fr: "Souris capturée — Échap la libère. Visez la note avec le cercle et cliquez pour la lire.",
    es: "Ratón capturado — Esc lo libera. Apunta el círculo a la nota y haz clic para leerla.",
  },
  // 2026-07-26 second audit pass (regression report: "over 50% of text is
  // still not translated") — these are the remaining hardcoded App.tsx
  // strings that render on every playthrough regardless of scenario
  // content (intro card chrome, desk hotspots, the guided/free-move
  // picker, and the title-beat tap hint).
  "whatif.label": {
    en: "What would have happened",
    de: "Was passiert wäre",
    fr: "Ce qui se serait passé",
    es: "Lo que habría pasado",
  },
  "guide.or": { en: "Or", de: "Oder", fr: "Ou", es: "O" },
  "replay.title": {
    en: "Replay: the unsafe choice",
    de: "Wiederholung: die unsichere Wahl",
    fr: "Rejeu : le choix risqué",
    es: "Repetición: la opción insegura",
  },
  "replay.body": {
    en: "This is not the safe way. You are only replaying it to see what would have happened, so it is fine to click the risky option here. Nothing is scored.",
    de: "Das ist nicht der sichere Weg. Du spielst ihn nur nach, um zu sehen, was passiert wäre. Hier darfst du die riskante Option ruhig anklicken. Nichts wird bewertet.",
    fr: "Ce n'est pas la bonne façon de faire. Vous rejouez cette scène seulement pour voir ce qui se serait passé : vous pouvez cliquer ici sur l'option risquée. Rien n'est noté.",
    es: "Este no es el camino seguro. Solo lo repites para ver lo que habría pasado, así que aquí puedes hacer clic en la opción arriesgada. No se puntúa nada.",
  },
  "replay.bodyView": {
    en: "This is not the safe way. You are only seeing what would have happened after the risky choice. Nothing is scored.",
    de: "Das ist nicht der sichere Weg. Du siehst nur, was nach der riskanten Wahl passiert wäre. Nichts wird bewertet.",
    fr: "Ce n'est pas la bonne façon de faire. Vous voyez seulement ce qui se serait passé après le choix risqué. Rien n'est noté.",
    es: "Este no es el camino seguro. Solo ves lo que habría pasado tras la opción arriesgada. No se puntúa nada.",
  },
  "intro.situation": {
    en: "Your situation",
    de: "Deine Situation",
    fr: "Votre situation",
    es: "Tu situación",
  },
  "intro.goal": {
    en: "Your goal",
    de: "Dein Ziel",
    fr: "Votre objectif",
    es: "Tu objetivo",
  },
  "intro.youDo": {
    en: "What you'll do",
    de: "Das machst du",
    fr: "Ce que vous allez faire",
    es: "Lo que harás",
  },
  "intro.topics": {
    en: "Topics",
    de: "Themen",
    fr: "Thèmes",
    es: "Temas",
  },
  "intro.quizCount": {
    en: "Quiz: {n} questions",
    de: "Quiz: {n} Fragen",
    fr: "Quiz : {n} questions",
    es: "Cuestionario: {n} preguntas",
  },
  "intro.need.sound": {
    en: "Best with sound on",
    de: "Am besten mit Ton",
    fr: "Idéal avec le son",
    es: "Mejor con sonido",
  },
  "intro.need.microphone_optional": {
    en: "Microphone optional",
    de: "Mikrofon optional",
    fr: "Micro facultatif",
    es: "Micrófono opcional",
  },
  "intro.need.keyboard_walk": {
    en: "Walk with W/A/S/D",
    de: "Gehen mit W/A/S/D",
    fr: "Se déplacer avec W/A/S/D",
    es: "Camina con W/A/S/D",
  },
  "intro.beginMission": {
    en: "Begin mission",
    de: "Mission starten",
    fr: "Commencer la mission",
    es: "Iniciar misión",
  },
  "intro.loadingRoom": {
    en: "Loading the 3D room",
    de: "3D-Raum wird geladen",
    fr: "Chargement de la salle 3D",
    es: "Cargando la sala 3D",
  },
  "intro.preparingRoom": {
    en: "Preparing the 3D room",
    de: "3D-Raum wird vorbereitet",
    fr: "Préparation de la salle 3D",
    es: "Preparando la sala 3D",
  },
  "intro.roomReady": {
    en: "3D room ready",
    de: "3D-Raum bereit",
    fr: "Salle 3D prête",
    es: "Sala 3D lista",
  },
  "intro.missionBriefing": {
    en: "Mission briefing",
    de: "Missionsbriefing",
    fr: "Briefing de mission",
    es: "Briefing de la misión",
  },
  "desk.readNote": {
    en: "Read note",
    de: "Notiz lesen",
    fr: "Lire la note",
    es: "Leer la nota",
  },
  "desk.clickToReadNote": {
    en: "Click to read the note",
    de: "Klicken, um die Notiz zu lesen",
    fr: "Cliquez pour lire la note",
    es: "Haz clic para leer la nota",
  },
  "desk.switchOnTvTitle": {
    en: "Switch on the TV",
    de: "Fernseher einschalten",
    fr: "Allumer la télé",
    es: "Encender el televisor",
  },
  "desk.switchOnTv": {
    en: "Switch on TV",
    de: "TV einschalten",
    fr: "Allumer la télé",
    es: "Encender TV",
  },
  "desk.openPackageTitle": {
    en: "Open the package",
    de: "Das Paket öffnen",
    fr: "Ouvrir le colis",
    es: "Abrir el paquete",
  },
  "desk.clickToOpen": {
    en: "Click to open",
    de: "Klicken zum Öffnen",
    fr: "Cliquez pour ouvrir",
    es: "Haz clic para abrir",
  },
  "nav.help": {
    en: "Help",
    de: "Hilfe",
    fr: "Aide",
    es: "Ayuda",
  },
  "nav.howToMoveTitle": {
    en: "How to move & look around",
    de: "Wie du dich bewegst und umschaust",
    fr: "Comment se déplacer et regarder autour de soi",
    es: "Cómo moverte y mirar alrededor",
  },
  "music.on": {
    en: "Music on",
    de: "Musik an",
    fr: "Musique activée",
    es: "Música activada",
  },
  "music.off": {
    en: "Music off",
    de: "Musik aus",
    fr: "Musique désactivée",
    es: "Música desactivada",
  },
  "voice.on": {
    en: "Voice on",
    de: "Stimme an",
    fr: "Voix activée",
    es: "Voz activada",
  },
  "voice.off": {
    en: "Voice off",
    de: "Stimme aus",
    fr: "Voix désactivée",
    es: "Voz desactivada",
  },
  "mode.howExplore": {
    en: "How do you want to explore?",
    de: "Wie möchtest du erkunden?",
    fr: "Comment voulez-vous explorer ?",
    es: "¿Cómo quieres explorar?",
  },
  "mode.guidedTour": {
    en: "Guided tour",
    de: "Geführte Tour",
    fr: "Visite guidée",
    es: "Visita guiada",
  },
  "mode.guidedTourDesc": {
    en: "The camera moves for you, scene to scene.",
    de: "Die Kamera bewegt sich für dich, Szene für Szene.",
    fr: "La caméra se déplace pour vous, scène après scène.",
    es: "La cámara se mueve por ti, de escena en escena.",
  },
  "mode.freeMovement": {
    en: "Free movement",
    de: "Freie Bewegung",
    fr: "Mouvement libre",
    es: "Movimiento libre",
  },
  "mode.freeMovementDesc": {
    en: "Walk the office yourself with W / A / S / D.",
    de: "Bewege dich selbst mit W / A / S / D durchs Büro.",
    fr: "Déplacez-vous vous-même dans le bureau avec Z / Q / S / D.",
    es: "Recorre la oficina tú mismo con W / A / S / D.",
  },
  "mode.switchHint": {
    en: "You can switch anytime from the controls at the bottom.",
    de: "Du kannst jederzeit über die Steuerung unten wechseln.",
    fr: "Vous pouvez changer à tout moment depuis les commandes en bas.",
    es: "Puedes cambiar en cualquier momento desde los controles de abajo.",
  },
  "title.tapToContinue": {
    en: "Tap to continue",
    de: "Tippen, um fortzufahren",
    fr: "Touchez pour continuer",
    es: "Toca para continuar",
  },

  "priv.kicker": { en: "Office privacy · Explore & learn", de: "Büro-Datenschutz · Entdecken & lernen", fr: "Confidentialité au bureau · Explorer", es: "Privacidad en la oficina · Explorar" },
  "priv.title": { en: "Explore the office", de: "Erkunde das Büro", fr: "Explorez le bureau", es: "Explora la oficina" },
  "priv.description": { en: "Click the objects you find around the room. Each card explains what you found, whether it can stay in the open, and how to handle it.", de: "Klicke die Gegenstände an, die du im Raum findest. Jede Karte erklärt, was du gefunden hast, ob es offen liegen bleiben darf und wie du damit umgehst.", fr: "Cliquez sur les objets que vous trouvez dans la pièce. Chaque fiche explique ce que vous avez trouvé, si cela peut rester en vue et comment le traiter.", es: "Haz clic en los objetos que encuentres en la sala. Cada ficha explica qué has encontrado, si puede quedar a la vista y cómo manejarlo." },
  "priv.goal": { en: "Your goal is to discover every item. There are no right or wrong clicks.", de: "Dein Ziel ist, jeden Gegenstand zu entdecken. Es gibt keine richtigen oder falschen Klicks.", fr: "Votre objectif est de découvrir chaque objet. Il n'y a pas de clic correct ou incorrect.", es: "Tu objetivo es descubrir todos los objetos. No hay clics correctos ni incorrectos." },
  "priv.speak": { en: "Click the objects you find around the room. Each card explains what you found, whether it can stay in the open, and how to handle it. Your goal is to discover every item. There are no right or wrong clicks.", de: "Klicke die Gegenstände an, die du im Raum findest. Jede Karte erklärt, was du gefunden hast, ob es offen liegen bleiben darf und wie du damit umgehst. Dein Ziel ist, jeden Gegenstand zu entdecken. Es gibt keine richtigen oder falschen Klicks.", fr: "Cliquez sur les objets que vous trouvez dans la pièce. Chaque fiche explique ce que vous avez trouvé, si cela peut rester en vue et comment le traiter. Votre objectif est de découvrir chaque objet. Il n'y a pas de clic correct ou incorrect.", es: "Haz clic en los objetos que encuentres en la sala. Cada ficha explica qué has encontrado, si puede quedar a la vista y cómo manejarlo. Tu objetivo es descubrir todos los objetos. No hay clics correctos ni incorrectos." },
  "priv.hint": { en: "Click an object to open its learning card.", de: "Klicke ein Objekt an, um seine Lernkarte zu öffnen.", fr: "Cliquez sur un objet pour ouvrir sa fiche.", es: "Haz clic en un objeto para abrir su ficha." },
  "priv.progress": { en: "Items explored {explored} / {total}", de: "Entdeckt {explored} / {total}", fr: "Objets explorés {explored} / {total}", es: "Objetos explorados {explored} / {total}" },
  "priv.continue": { en: "Continue to sorting", de: "Weiter zum Sortieren", fr: "Continuer vers le tri", es: "Continuar a clasificar" },
  "priv.done": { en: "You've explored all {total} items. Continue to sorting to practise what you've learned.", de: "Du hast alle {total} Gegenstände entdeckt. Geh weiter zum Sortieren und übe, was du gelernt hast.", fr: "Vous avez exploré les {total} objets. Passez au tri pour vous exercer.", es: "Has explorado los {total} objetos. Continúa a clasificar para practicar lo aprendido." },
  "priv.instructions": { en: "Instructions", de: "Anleitung", fr: "Instructions", es: "Instrucciones" },
  "priv.list": { en: "All items", de: "Alle Gegenstände", fr: "Tous les objets", es: "Todos los objetos" },
  "priv.explored": { en: "Explored", de: "Entdeckt", fr: "Exploré", es: "Explorado" },
  "priv.close": { en: "Close", de: "Schliessen", fr: "Fermer", es: "Cerrar" },
  "priv.typeLabel": { en: "Information type", de: "Art der Information", fr: "Type d'information", es: "Tipo de información" },
  "priv.classLabel": { en: "Classification", de: "Einstufung", fr: "Classification", es: "Clasificación" },
  "priv.found": { en: "What you found", de: "Was du gefunden hast", fr: "Ce que vous avez trouvé", es: "Qué has encontrado" },
  "priv.matters": { en: "Why it matters", de: "Warum es wichtig ist", fr: "Pourquoi c'est important", es: "Por qué importa" },
  "priv.action": { en: "What to do", de: "Was zu tun ist", fr: "Que faire", es: "Qué hacer" },
  "priv.keep": { en: "Keep exploring", de: "Weiter entdecken", fr: "Continuer l'exploration", es: "Seguir explorando" },
  "priv.protect": { en: "Put away securely", de: "Sicher weglegen", fr: "Ranger en lieu sûr", es: "Guardar de forma segura" },
};

export function t(key: keyof typeof STRINGS): string {
  const entry = STRINGS[key];
  if (!entry) return key;
  const locale = getActiveLocale() as Locale;
  return entry[locale] ?? entry.en;
}
